import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

const SCROLL_STEP = 120;
const ARROW_BUTTON_HEIGHT = 32;
const MIN_THUMB_HEIGHT = 24;

const CustomScrollbar = ({ scrollContainerRef, className = '' }) => {
  const [scrollTop, setScrollTop] = useState(0);
  const [scrollHeight, setScrollHeight] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const trackRef = useRef(null);

  const updateFromContainer = useCallback(() => {
    const el = scrollContainerRef?.current;
    if (!el) return;
    setScrollTop(el.scrollTop);
    setScrollHeight(el.scrollHeight);
    setClientHeight(el.clientHeight);
  }, [scrollContainerRef]);

  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el) return;
    updateFromContainer();
    el.addEventListener('scroll', updateFromContainer);
    const ro = new ResizeObserver(updateFromContainer);
    ro.observe(el);
    const pollId = setInterval(updateFromContainer, 150);
    const stopPolling = () => clearInterval(pollId);
    const t = setTimeout(stopPolling, 2500);
    return () => {
      el.removeEventListener('scroll', updateFromContainer);
      ro.disconnect();
      stopPolling();
      clearTimeout(t);
    };
  }, [scrollContainerRef, updateFromContainer]);

  const scrollBy = useCallback(
    (delta) => {
      const el = scrollContainerRef?.current;
      if (!el) return;
      el.scrollTop = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, el.scrollTop + delta));
    },
    [scrollContainerRef]
  );

  const handleUp = () => scrollBy(-SCROLL_STEP);
  const handleDown = () => scrollBy(SCROLL_STEP);

  const handleTrackClick = (e) => {
    const el = scrollContainerRef?.current;
    const track = trackRef.current;
    if (!el || !track || scrollHeight <= clientHeight) return;
    const rect = track.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const trackHeight = rect.height;
    const ratio = y / trackHeight;
    const maxScroll = el.scrollHeight - el.clientHeight;
    el.scrollTop = ratio * maxScroll;
  };

  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    if (scrollHeight <= clientHeight) return;
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;
    const maxScroll = Math.max(0, scrollHeight - clientHeight);
    const trackHeight = clientHeight - 2 * ARROW_BUTTON_HEIGHT;

    const onMouseMove = (e) => {
      const el = scrollContainerRef?.current;
      const track = trackRef.current;
      if (!el || !track || maxScroll <= 0) return;
      const rect = track.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const ratio = Math.max(0, Math.min(1, y / trackHeight));
      el.scrollTop = ratio * maxScroll;
    };

    const onMouseUp = () => setIsDragging(false);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging, scrollHeight, clientHeight, scrollContainerRef]);

  const canScroll = scrollHeight > clientHeight;
  const trackHeight = Math.max(0, clientHeight - 2 * ARROW_BUTTON_HEIGHT);
  const thumbHeight = trackHeight <= 0 ? 0 : Math.max(MIN_THUMB_HEIGHT, (clientHeight / scrollHeight) * trackHeight);
  const maxScroll = Math.max(0, scrollHeight - clientHeight);
  const thumbTop =
    maxScroll > 0 ? (scrollTop / maxScroll) * (trackHeight - thumbHeight) : 0;

  return (
    <div
      className={`flex flex-col items-stretch w-6 flex-shrink-0 bg-[#E8EBE9] dark:bg-[#282A29] border-l border-[#C3C4C3] dark:border-[#3a3c3b] ${className}`}
      role="scrollbar"
      aria-valuenow={maxScroll ? Math.round((scrollTop / maxScroll) * 100) : 0}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-controls="bloom-terminal-main"
    >
      <button
        type="button"
        onClick={handleUp}
        disabled={!canScroll || scrollTop <= 0}
        className="h-8 flex items-center justify-center text-[#5D625F] dark:text-gray-400 hover:bg-[#d0d4d1] dark:hover:bg-[#3a3c3b] disabled:opacity-40 disabled:pointer-events-none transition-colors"
        aria-label="Scroll up"
      >
        <ChevronUp size={20} strokeWidth={2} />
      </button>
      <div
        ref={trackRef}
        className="flex-1 min-h-0 relative cursor-pointer"
        style={{ minHeight: trackHeight }}
        onClick={handleTrackClick}
      >
        {canScroll && (
          <div
            className="absolute left-1 right-1 rounded-md bg-[#5D625F] dark:bg-[#5d625f] hover:bg-bloom-green dark:hover:bg-bloom-green transition-colors cursor-grab active:cursor-grabbing"
            style={{
              top: thumbTop,
              height: thumbHeight,
            }}
            onMouseDown={handleThumbMouseDown}
            role="slider"
            aria-valuenow={maxScroll ? Math.round((scrollTop / maxScroll) * 100) : 0}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        )}
      </div>
      <button
        type="button"
        onClick={handleDown}
        disabled={!canScroll || scrollTop >= maxScroll}
        className="h-8 flex items-center justify-center text-[#5D625F] dark:text-gray-400 hover:bg-[#d0d4d1] dark:hover:bg-[#3a3c3b] disabled:opacity-40 disabled:pointer-events-none transition-colors"
        aria-label="Scroll down"
      >
        <ChevronDown size={20} strokeWidth={2} />
      </button>
    </div>
  );
};

export default CustomScrollbar;
